import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { setSessionToken, clearSessionToken } from "./api";

const AuthContext = createContext(null);
const PUBLIC_AUTH_ROUTES = new Set(["/", "/login", "/forgot-password", "/reset-password", "/verify-email", "/set-password", "/register-venue"]);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const currentPath = window.location.pathname;
    const hasSessionToken = Boolean(localStorage.getItem("setplay_session_token"));

    if (PUBLIC_AUTH_ROUTES.has(currentPath) && !hasSessionToken) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const res = await api.get("/auth/me", {
        validateStatus: (status) => status === 200 || status === 401,
      });
      setUser(res.status === 200 ? res.data : null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const loginWithEmail = async (email, password, persona_type = null) => {
    const payload = { email, password };
    if (persona_type) payload.persona_type = persona_type;
    const res = await api.post("/auth/login", payload);
    if (res.data.session_token) {
      setSessionToken(res.data.session_token);
    }
    setUser(res.data);
    return res.data;
  };

  const register = async (name, email, password, role = "USER", referral_code = "") => {
    const payload = { name, email, password, role };
    if (referral_code) payload.referral_code = referral_code;
    const res = await api.post("/auth/register", payload);
    if (res.data.session_token) {
      setSessionToken(res.data.session_token);
    }
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearSessionToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, loginWithEmail, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
